/**
 * OrganizationDashboard.js — Organizations tab
 *
 * Privacy-preserving org reporting for corporate clients.
 * All metrics are aggregate-only — no individual user data surfaced.
 *
 * Layout:
 *   - Org management table (list, add, edit, deactivate)
 *   - Org selector dropdown + time filter
 *   - 3 metric cards: Seats, Active users, Thought rate
 *   - Stacked bar chart: org-scoped daily entries (focus-only vs with-thought)
 *   - Box-and-whisker chart placeholder (pending Phase 2.2 distribution endpoint)
 *   - Privacy notice banner
 *
 * Data sources:
 *   adminService.getOrganizations()              — org list for selector + table
 *   adminService.getOrganizationMetrics(orgId)    — scoped metrics + daily chart
 *   adminService.createOrganization(data)         — CRUD
 *   adminService.updateOrganization(orgId, data)  — CRUD
 *   adminService.deleteOrganization(orgId)        — soft delete
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer
} from 'recharts';
import { Plus, X, Edit2, Trash2, Shield, Users, UserPlus, UserX, Clock } from 'lucide-react';
import { apiService } from '../../services/api';
import BulkEnrollUpload from './BulkEnrollUpload';

// =========================================
// Constants
// =========================================

const COLORS = {
  blue:   '#3B8BD4',
  green:  '#1D9E75',
  amber:  '#EF9F27',
  coral:  '#D85A30',
};

const TIMEFRAMES = [
  { key: '7d',   label: '1W' },
  { key: '30d',  label: '1M' },
  { key: '90d',  label: '3M' },
  { key: '365d', label: '1Y' },
];

const PLAN_OPTIONS = [
  { value: 'freemium',   label: 'Freemium' },
  { value: 'trial',      label: 'Trial' },
  { value: 'standard',   label: 'Standard' },
  { value: 'enterprise', label: 'Enterprise' },
];

const formatDateTick = (value) => {
  const date = new Date(value + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// =========================================
// Org Form (Add / Edit)
// =========================================

const EMPTY_FORM = {
  name: '',
  domain: '',
  contactEmail: '',
  plan: 'trial',
  seatLimit: '',
  notes: '',
};

const OrgForm = ({ initial, onSubmit, onCancel, isEditing }) => {
  const [form, setForm] = useState(initial || EMPTY_FORM);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (!form.name.trim() || (!isEditing && !form.domain.trim())) return;
    const payload = {
      ...form,
      seatLimit: form.seatLimit === '' ? null : Number(form.seatLimit),
    };
    onSubmit(payload);
  };

  return (
    <div className="add-waitlist-entry">
      <h3>{isEditing ? 'Edit Organization' : 'Add Organization'}</h3>
      <div className="form-row">
        <div className="form-field">
          <label>Organization Name *</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="e.g., Girl Security"
          />
        </div>
        <div className="form-field">
          <label>Email Domain {isEditing ? '(read-only)' : '*'}</label>
          <input
            type="text"
            value={form.domain}
            onChange={(e) => handleChange('domain', e.target.value)}
            placeholder="e.g., girlsecurity.org"
            disabled={isEditing}
          />
        </div>
      </div>
      <div className="form-row">
        <div className="form-field">
          <label>Contact Email</label>
          <input
            type="email"
            value={form.contactEmail}
            onChange={(e) => handleChange('contactEmail', e.target.value)}
            placeholder="primary@example.org"
          />
        </div>
        <div className="form-field">
          <label>Plan</label>
          <select
            value={form.plan}
            onChange={(e) => handleChange('plan', e.target.value)}
            className="form-select"
          >
            {PLAN_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label>Seat Limit</label>
          <input
            type="number"
            value={form.seatLimit}
            onChange={(e) => handleChange('seatLimit', e.target.value)}
            placeholder="Unlimited"
            min="0"
          />
        </div>
      </div>
      <div className="form-field">
        <label>Notes</label>
        <textarea
          value={form.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
          placeholder="Admin notes about this organization..."
          rows={2}
        />
      </div>
      <div className="form-actions">
        <button className="deny-button" onClick={onCancel} style={{ marginRight: 8 }}>
          Cancel
        </button>
        <button
          className="add-button"
          onClick={handleSubmit}
          disabled={!form.name.trim() || (!isEditing && !form.domain.trim())}
        >
          {isEditing ? 'Save Changes' : 'Add Organization'}
        </button>
      </div>
    </div>
  );
};

// =========================================
// Stacked Bar Tooltip
// =========================================

const OrgBarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const focusOnly = payload.find(p => p.dataKey === 'focusOnly');
  const withThought = payload.find(p => p.dataKey === 'withThought');
  const total = (focusOnly?.value || 0) + (withThought?.value || 0);

  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip-label">{formatDateTick(label)}</p>
      <p style={{ color: COLORS.blue }}>Focus only: {focusOnly?.value || 0}</p>
      <p style={{ color: COLORS.green }}>With thought: {withThought?.value || 0}</p>
      <p className="chart-tooltip-total">Total: {total}</p>
    </div>
  );
};

// =========================================
// Admin Error Extraction
// =========================================

/**
 * Extract a user-facing error message from a failed axios request.
 * The admin "already exists" 400 returns an enriched payload of shape
 *   { message, existingUser: { tier, isActive, isVerified, createdAt } }
 * The server-provided `message` already includes the tier/createdAt summary.
 * Older responses that only carry `message` fall through the same path.
 */
const extractAdminErrorMessage = (error, fallback) => {
  return error?.response?.data?.message || error?.message || fallback;
};

// =========================================
// Main Component
// =========================================

const OrganizationDashboard = () => {
  // Org list + CRUD state
  const [orgs, setOrgs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);

  // Selected org + metrics
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [timeframe, setTimeframe] = useState('30d');
  const [metrics, setMetrics] = useState(null);
  const [chartData, setChartData] = useState([]);

  // Seat management
  const [seatData, setSeatData] = useState(null);
  const [seatsLoading, setSeatsLoading] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserFirst, setNewUserFirst] = useState('');
  const [newUserLast, setNewUserLast] = useState('');
  const [addingUser, setAddingUser] = useState(false);

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // ------------------------------------------
  // Load org list
  // ------------------------------------------

  const fetchOrgs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiService.admin.getOrganizations();
      setOrgs(data.filter(o => o.isActive !== false));
    } catch (err) {
      console.error('Error fetching organizations:', err);
      setError('Failed to load organizations.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  // ------------------------------------------
  // Load org metrics when selection changes
  // ------------------------------------------

  const fetchOrgMetrics = useCallback(async () => {
    if (!selectedOrgId) {
      setMetrics(null);
      setChartData([]);
      return;
    }

    setMetricsLoading(true);
    try {
      const data = await apiService.admin.getOrganizationMetrics(selectedOrgId, timeframe);
      setMetrics(data);

      const daily = (data.dailyEntries || []).map(day => {
        const entries = day.entries || 0;
        const thoughts = day.withThoughts || 0;
        return {
          date: day._id,
          focusOnly: Math.max(entries - thoughts, 0),
          withThought: thoughts,
        };
      });
      setChartData(daily);
    } catch (err) {
      console.error('Error fetching org metrics:', err);
      setMetrics(null);
      setChartData([]);
    } finally {
      setMetricsLoading(false);
    }
  }, [selectedOrgId, timeframe]);

  useEffect(() => { fetchOrgMetrics(); }, [fetchOrgMetrics]);

  // ------------------------------------------
  // Load seat data when org changes
  // ------------------------------------------

  const fetchSeatData = useCallback(async () => {
    if (!selectedOrgId) {
      setSeatData(null);
      return;
    }

    setSeatsLoading(true);
    try {
      const data = await apiService.admin.getOrganizationUsers(selectedOrgId);
      setSeatData(data);
    } catch (err) {
      console.error('Error fetching seat data:', err);
      setSeatData(null);
    } finally {
      setSeatsLoading(false);
    }
  }, [selectedOrgId]);

  useEffect(() => { fetchSeatData(); }, [fetchSeatData]);

  // ------------------------------------------
  // Seat management handlers
  // ------------------------------------------

  const validateNewUserEmail = () => {
    const selectedOrg = orgs.find(o => o.orgId === selectedOrgId);
    if (!selectedOrg || !newUserEmail.trim()) return null;
    const emailDomain = newUserEmail.trim().split('@')[1]?.toLowerCase();
    if (emailDomain !== selectedOrg.domain.toLowerCase()) {
      setError(`Email must match domain @${selectedOrg.domain}`);
      setTimeout(() => setError(null), 4000);
      return null;
    }
    return {
      email: newUserEmail.trim(),
      firstName: newUserFirst.trim(),
      lastName: newUserLast.trim(),
    };
  };

  const clearAddForm = () => {
    setNewUserEmail('');
    setNewUserFirst('');
    setNewUserLast('');
    setShowAddUser(false);
  };

  const handleAddToWaitlist = async () => {
    const userData = validateNewUserEmail();
    if (!userData) return;

    setAddingUser(true);
    try {
      await apiService.admin.addToWaitlist(userData);
      setSuccessMsg(`${userData.email} added to waitlist (pending review).`);
      setTimeout(() => setSuccessMsg(null), 3000);
      clearAddForm();
      fetchSeatData();
    } catch (err) {
      setError(extractAdminErrorMessage(err, 'Failed to add to waitlist.'));
      setTimeout(() => setError(null), 4000);
    } finally {
      setAddingUser(false);
    }
  };

  const handleAddAndApprove = async () => {
    const userData = validateNewUserEmail();
    if (!userData) return;

    setAddingUser(true);
    try {
      await apiService.admin.addAndApproveWaitlist(userData);
      setSuccessMsg(`${userData.email} added and approved.`);
      setTimeout(() => setSuccessMsg(null), 3000);
      clearAddForm();
      fetchSeatData();
      fetchOrgMetrics();
    } catch (err) {
      setError(extractAdminErrorMessage(err, 'Failed to add user.'));
      setTimeout(() => setError(null), 4000);
    } finally {
      setAddingUser(false);
    }
  };

  const handleDeactivateUser = async (user) => {
    if (!window.confirm(`Deactivate account for ${user.email}? They will lose access but data is preserved.`)) {
      return;
    }
    try {
      await apiService.admin.deactivateUser(selectedOrgId, user.userId);
      setSuccessMsg(`${user.email} deactivated.`);
      setTimeout(() => setSuccessMsg(null), 3000);
      fetchSeatData();
      fetchOrgMetrics();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to deactivate user.';
      setError(msg);
      setTimeout(() => setError(null), 4000);
    }
  };

  // ------------------------------------------
  // CRUD Handlers
  // ------------------------------------------

  const handleCreate = async (orgData) => {
    try {
      await apiService.admin.createOrganization(orgData);
      setShowForm(false);
      setSuccessMsg('Organization created successfully.');
      setTimeout(() => setSuccessMsg(null), 3000);
      fetchOrgs();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to create organization.';
      setError(msg);
      setTimeout(() => setError(null), 4000);
    }
  };

  const handleUpdate = async (orgData) => {
    if (!editingOrg) return;
    try {
      await apiService.admin.updateOrganization(editingOrg.orgId, orgData);
      setEditingOrg(null);
      setSuccessMsg('Organization updated.');
      setTimeout(() => setSuccessMsg(null), 3000);
      fetchOrgs();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update organization.';
      setError(msg);
      setTimeout(() => setError(null), 4000);
    }
  };

  const handleDeactivate = async (org) => {
    if (!window.confirm(`Deactivate "${org.name}"? This hides it from reporting but doesn't delete data.`)) {
      return;
    }
    try {
      await apiService.admin.deleteOrganization(org.orgId);
      setSuccessMsg(`"${org.name}" deactivated.`);
      setTimeout(() => setSuccessMsg(null), 3000);
      if (selectedOrgId === org.orgId) setSelectedOrgId('');
      fetchOrgs();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to deactivate organization.';
      setError(msg);
      setTimeout(() => setError(null), 4000);
    }
  };

  const startEditing = (org) => {
    setEditingOrg(org);
    setShowForm(false);
  };

  // ------------------------------------------
  // Render
  // ------------------------------------------

  if (isLoading) {
    return <div className="loading-message">Loading organizations…</div>;
  }

  return (
    <div className="org-dashboard">
      {/* Messages */}
      {successMsg && <div className="success-message">{successMsg}</div>}
      {error && <div className="error-message">{error}</div>}

      {/* ============ ORG MANAGEMENT ============ */}
      <div className="org-management">
        <div className="org-management-header">
          <h2>Client Organizations</h2>
          <button
            className={`add-user-toggle ${showForm ? 'active' : ''}`}
            onClick={() => { setShowForm(!showForm); setEditingOrg(null); }}
          >
            {showForm ? <X size={16} /> : <Plus size={16} />}
            <span>{showForm ? 'Cancel' : 'Add Organization'}</span>
          </button>
        </div>

        {/* Add form */}
        {showForm && (
          <OrgForm
            onSubmit={handleCreate}
            onCancel={() => setShowForm(false)}
            isEditing={false}
          />
        )}

        {/* Edit form */}
        {editingOrg && (
          <OrgForm
            initial={{
              name: editingOrg.name,
              domain: editingOrg.domain,
              contactEmail: editingOrg.contactEmail || '',
              plan: editingOrg.plan || 'trial',
              seatLimit: editingOrg.seatLimit != null ? String(editingOrg.seatLimit) : '',
              notes: editingOrg.notes || '',
            }}
            onSubmit={handleUpdate}
            onCancel={() => setEditingOrg(null)}
            isEditing={true}
          />
        )}

        {/* Org table */}
        {orgs.length > 0 ? (
          <table className="waitlist-table org-table">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Domain</th>
                <th>Plan</th>
                <th>Contact</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orgs.map(org => (
                <tr key={org.orgId}>
                  <td>{org.name}</td>
                  <td>{org.domain}</td>
                  <td>
                    <span className={`status-badge ${org.plan}`}>
                      {org.plan}
                    </span>
                  </td>
                  <td>{org.contactEmail || '—'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="approve-button"
                        onClick={() => startEditing(org)}
                        title="Edit organization"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        className="deny-button"
                        onClick={() => handleDeactivate(org)}
                        title="Deactivate organization"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="org-empty-state">
            No organizations registered yet. Add one to start tracking client metrics.
          </p>
        )}
      </div>

      {/* ============ BULK ENROLLMENT ============ */}
      <BulkEnrollUpload onComplete={fetchOrgs} />

      {/* ============ ORG METRICS ============ */}
      {orgs.length > 0 && (
        <div className="org-metrics-section">
          <div className="org-toolbar">
            <div className="org-selector">
              <label>Organization:</label>
              <select
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
              >
                <option value="">Select an organization…</option>
                {orgs.map(org => (
                  <option key={org.orgId} value={org.orgId}>
                    {org.name} ({org.domain})
                  </option>
                ))}
              </select>
            </div>

            {selectedOrgId && (
              <div className="time-filter">
                {TIMEFRAMES.map(tf => (
                  <button
                    key={tf.key}
                    className={timeframe === tf.key ? 'active' : ''}
                    onClick={() => setTimeframe(tf.key)}
                  >
                    {tf.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Metrics content */}
          {selectedOrgId && (
            metricsLoading ? (
              <div className="loading-message">Loading metrics…</div>
            ) : metrics ? (
              <>
                {/* Metric cards */}
                <div className="org-metric-cards">
                  <div className="metric-card">
                    <h3>Seats</h3>
                    <div className="metric-value">
                      {metrics.seats}
                      {metrics.seatLimit != null && (
                        <span className="metric-suffix"> / {metrics.seatLimit}</span>
                      )}
                    </div>
                  </div>
                  <div className="metric-card">
                    <h3>Active (≤ 3d)</h3>
                    <div className="metric-value" style={{ color: COLORS.green }}>
                      {metrics.activity?.active || 0}
                    </div>
                  </div>
                  <div className="metric-card">
                    <h3>Thought Rate</h3>
                    <div className="metric-value">
                      {metrics.entries?.thoughtRate || 0}%
                    </div>
                  </div>
                </div>

                {/* ============ SEATS PANEL ============ */}
                <div className="seats-panel">
                  <div className="seats-header">
                    <h3><Users size={16} /> Seats</h3>
                    <button
                      className={`add-user-toggle small ${showAddUser ? 'active' : ''}`}
                      onClick={() => setShowAddUser(!showAddUser)}
                    >
                      {showAddUser ? <X size={14} /> : <UserPlus size={14} />}
                      <span>{showAddUser ? 'Cancel' : 'Add User'}</span>
                    </button>
                  </div>

                  {/* Inline add-user form */}
                  {showAddUser && (
                    <div className="seats-add-form">
                      <div className="form-row">
                        <input
                          type="text"
                          placeholder="First name"
                          value={newUserFirst}
                          onChange={(e) => setNewUserFirst(e.target.value)}
                        />
                        <input
                          type="text"
                          placeholder="Last name"
                          value={newUserLast}
                          onChange={(e) => setNewUserLast(e.target.value)}
                        />
                        <input
                          type="email"
                          placeholder={`user@${metrics.org?.domain || 'domain.com'}`}
                          value={newUserEmail}
                          onChange={(e) => setNewUserEmail(e.target.value)}
                        />
                        <button
                          className="add-button"
                          onClick={handleAddToWaitlist}
                          disabled={addingUser || !newUserEmail.trim()}
                        >
                          {addingUser ? 'Adding…' : 'Add to Waitlist'}
                        </button>
                        <button
                          className="add-button secondary"
                          onClick={handleAddAndApprove}
                          disabled={addingUser || !newUserEmail.trim()}
                        >
                          Add & Approve
                        </button>
                      </div>
                    </div>
                  )}

                  {seatsLoading ? (
                    <div className="loading-message">Loading users…</div>
                  ) : seatData ? (
                    <>
                      {/* Pending waitlist entries */}
                      {seatData.pending?.length > 0 && (
                        <div className="seats-pending">
                          <h4><Clock size={14} /> Pending Waitlist ({seatData.pending.length})</h4>
                          <table className="waitlist-table seats-table">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Requested</th>
                              </tr>
                            </thead>
                            <tbody>
                              {seatData.pending.map((entry, i) => (
                                <tr key={i}>
                                  <td>{[entry.firstName, entry.lastName].filter(Boolean).join(' ') || '—'}</td>
                                  <td>{entry.email}</td>
                                  <td>{new Date(entry.requestedAt).toLocaleDateString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Registered users */}
                      {seatData.users?.length > 0 ? (
                        <table className="waitlist-table seats-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Status</th>
                              <th>Last Active</th>
                              <th>Joined</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {seatData.users.map(user => (
                              <tr key={user.userId} className={!user.isActive ? 'deactivated-row' : ''}>
                                <td>{[user.firstName, user.lastName].filter(Boolean).join(' ') || '—'}</td>
                                <td>{user.email}</td>
                                <td>
                                  <span className={`status-badge ${user.isActive ? user.activityStatus : 'deactivated'}`}>
                                    {user.isActive ? user.activityStatus : 'deactivated'}
                                  </span>
                                </td>
                                <td>{user.lastActive ? new Date(user.lastActive).toLocaleDateString() : '—'}</td>
                                <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                                <td>
                                  {user.isActive && (
                                    <button
                                      className="deny-button"
                                      onClick={() => handleDeactivateUser(user)}
                                      title="Deactivate user account"
                                    >
                                      <UserX size={14} />
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <p className="org-empty-state">
                          No registered users for this organization yet.
                        </p>
                      )}
                    </>
                  ) : null}
                </div>

                {/* Stacked bar chart */}
                {chartData.length > 0 ? (
                  <div className="chart-container">
                    <div className="chart-header">
                      <h2 className="chart-title">
                        {metrics.org?.name} — Knowledge Entries
                      </h2>
                    </div>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={chartData} barCategoryGap="20%">
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                        <XAxis
                          dataKey="date"
                          tickFormatter={formatDateTick}
                          tick={{ fontSize: 12, fill: '#888' }}
                        />
                        <YAxis
                          allowDecimals={false}
                          tick={{ fontSize: 12, fill: '#888' }}
                        />
                        <Tooltip content={<OrgBarTooltip />} />
                        <Legend
                          formatter={(value) =>
                            value === 'focusOnly' ? 'Focus only' : 'With thought'
                          }
                        />
                        <Bar dataKey="focusOnly" stackId="entries" fill={COLORS.blue} />
                        <Bar
                          dataKey="withThought"
                          stackId="entries"
                          fill={COLORS.green}
                          radius={[3, 3, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="org-empty-state">
                    No knowledge entries in this period for {metrics.org?.name}.
                  </div>
                )}

                {/* Box-plot placeholder (Phase 2.2 distribution endpoint required) */}
                <div className="org-boxplot-placeholder">
                  <h3>Engagement Distribution</h3>
                  <p>
                    Box-and-whisker chart showing weekly entries per user will appear here
                    once the distribution endpoint is built (Phase 2.2).
                  </p>
                </div>
              </>
            ) : null
          )}

          {/* Privacy notice */}
          <div className="org-privacy-notice">
            <Shield size={16} />
            <span>
              Showing aggregate metrics only. Individual user activity is not tracked or disclosed.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationDashboard;
